// Remove old PrimeVue CSS import - v4 uses theme presets instead
// import 'primevue/resources/primevue.min.css';
import './assets/styles/layout.scss';
import 'primeicons/primeicons.css';

import { createApp, reactive } from 'vue';
import router from './router';
import createStore from './store';
// Sentry is dynamically imported in production to avoid circular import / TDZ issues



import App from './App.vue';
import PrimeVue from 'primevue/config';
import Aura from '@primevue/themes/aura';
import { usePreset } from '@primeuix/styled';

// PrimeVue: components are auto-imported via unplugin-vue-components + PrimeVueResolver
import ConfirmationService from 'primevue/confirmationservice';
import DatePicker from 'primevue/datepicker';
import Ripple from 'primevue/ripple';
import Select from 'primevue/select';
import StyleClass from 'primevue/styleclass';
import Tabs from 'primevue/tabs';
import ToggleSwitch from 'primevue/toggleswitch';
import ToastService from 'primevue/toastservice';
import Tooltip from 'primevue/tooltip';

// Backward-compat aliases (PrimeVue 4 renames): keep for existing <Calendar>, <Dropdown>, etc.
// REMOVED: bulk component imports - now auto-imported on-demand (see vite.config Components + PrimeVueResolver)
// - Chips (use AutoComplete with multiple)
// - InlineMessage (use Message component)
// - DataViewLayoutOptions (use SelectButton)
// - TriStateCheckbox (use Checkbox with indeterminate)
// - TabMenu (use Tabs)
// - Steps (use Stepper)
// - TabView (use Tabs)
// - BadgeDirective (use OverlayBadge component)

import TZ from 'dayjs/plugin/timezone';
import dayjs from 'dayjs';
dayjs.extend(TZ);


import { DateUtil } from './plugins/dateUtil';
import { CacheUtil } from './plugins/cacheUtils';

router.beforeEach(function (_to, _from, next) {
  window.scrollTo(0, 0);
  next();
});

router.beforeEach(function (_to, _from, next) {
  window.scrollTo(0, 0);
  next();
});

const app = createApp(App);

// Dynamically import Sentry in production to avoid circular import / TDZ issues during bundling
if (import.meta.env.PROD && typeof window !== 'undefined') {
  (async () => {
    try {
      const Sentry = await import('@sentry/vue');
      Sentry.init({
        app,
        dsn: "https://73d75aab8d03d520c7bd75b7dad52840@o4510802177490944.ingest.us.sentry.io/4510802179457024",
        // Setting this option to true will send default PII data to Sentry.
        // For example, automatic IP address collection on events
        sendDefaultPii: true,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({}),
          // Add the Vue integration with component tracking
          Sentry.vueIntegration({
            tracingOptions: {
              trackComponents: true,
              timeout: 2000,
              hooks: ['mount', 'update'],
            },
          }),
        ],
        // Performance Monitoring
        tracesSampleRate: 1.0, // Capture 100% of transactions for performance monitoring
        // Session Replay
        replaysSessionSampleRate: 0.1, // Sample 10% of sessions
        replaysOnErrorSampleRate: 1.0, // Sample 100% of sessions with errors
      });
    } catch (err) {
      // Non-fatal: don't block app load if Sentry fails to initialize
      console.error('Sentry failed to initialize', err);
    }
  })();
}

app.provide('dayjs', dayjs);

app.config.globalProperties.$appState = reactive({ 
  theme: 'aura-light-blue',
  darkMode: false 
});

// Initialize theme - check localStorage for saved preference
const savedDarkMode = localStorage.getItem('darkMode') === 'true';
const initialPreset = savedDarkMode ? usePreset(Aura, { dark: true }) : Aura;

// PrimeVue v4 configuration with theme
app.use(PrimeVue, { 
  theme: {
    preset: initialPreset,
    options: {
      darkModeSelector: '.dark', // Use class selector for toggleable dark mode
      cssLayer: {
        name: 'primevue',
        order: 'theme, base, primevue'
      }
    }
  },
  ripple: true, 
  inputStyle: 'outlined'
});

// Update appState darkMode and set document class
app.config.globalProperties.$appState.darkMode = savedDarkMode;
if (savedDarkMode) {
  document.documentElement.classList.add('dark');
}
app.use(ConfirmationService);
app.use(ToastService);
app.use(router);
const store = createStore(app);
app.use(store);
app.config.globalProperties.$store = store;
// Let the router guard reach Vuex so it can keep auth state in sync with
// localStorage / token expiry.
// @ts-ignore
router.bindApp({ $store: store });
app.use(DateUtil);
app.use(CacheUtil);

app.directive('tooltip', Tooltip);
app.directive('ripple', Ripple);
app.directive('styleclass', StyleClass);

// Aliases for PrimeVue 4 renames (Calendar→DatePicker, Dropdown→Select, InputSwitch→ToggleSwitch, TabView→Tabs)
app.component('Calendar', DatePicker);
app.component('Dropdown', Select);
app.component('InputSwitch', ToggleSwitch);
app.component('TabView', Tabs);

app.mount('#app');
